import { useState, useCallback } from 'react';
import { AppError, handleApiError, logError, ErrorMessages } from '../utils/errors';

interface UseErrorHandlerReturn {
  error: AppError | null;
  hasError: boolean;
  errorMessage: string;
  errorTitle: string;
  handleError: (error: any, context?: Record<string, any>) => void;
  clearError: () => void;
  retry: (() => void) | null;
  setRetryFn: (fn: (() => void) | null) => void;
}

/**
 * Hook para gerenciamento centralizado de erros
 * 
 * @example
 * const { error, handleError, clearError } = useErrorHandler();
 * 
 * try {
 *   await apiCall()
 * } catch (err) {
 *   handleError(err, { action: 'fetchChamados', userId: user.id })
 * }
 */
export const useErrorHandler = (): UseErrorHandlerReturn => {
  const [error, setError] = useState<AppError | null>(null);
  const [retry, setRetryFn] = useState<(() => void) | null>(null);

  const handleError = useCallback((error: any, context?: Record<string, any>) => {
    const appError = handleApiError(error);
    
    // Log do erro
    logError(appError, context);
    
    // Atualiza o estado
    setError(appError);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setRetryFn(null);
  }, []);

  const hasError = error !== null;
  
  const errorMessage = error 
    ? error.message 
    : '';
    
  const errorTitle = error 
    ? ErrorMessages[error.code as keyof typeof ErrorMessages]?.title || 'Erro'
    : '';

  return {
    error,
    hasError,
    errorMessage,
    errorTitle,
    handleError,
    clearError,
    retry,
    setRetryFn
  };
};